// Dates in dynamic-module fields (DynamicModule, FKRecordModal, the list-view composer preview).
//
// Field metadata says how a field is edited (uI_Type), not what SQL type it is, so the value's
// shape decides, and the API makes the shape reliable: a SQL date column arrives as 'YYYY-MM-DD',
// a datetime / datetime2 column as ISO with a Z. A date is shown literally; a timestamp on the
// person's clock - as its local date for a 'date' or 'readonly' field (Rev_Date on a datetime2
// table), with the time for a 'datetime' field. A picked date is saved as 'YYYY-MM-DD' and the API
// stores it by the column's real type (a date as-is, a timestamp as local midnight).
import { formatDateOnly, formatTimestamp, formatTimestampDate, dayKey } from './dateTime';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

export const isDateOnlyValue = (v) => typeof v === 'string' && DATE_ONLY.test(v);
export const isTimestampValue = (v) => typeof v === 'string' && TIMESTAMP.test(v);

/** Display text for a date-like field value; anything that is not a date comes back unchanged. */
export const formatFieldDate = (value, uiType) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value !== 'string') return value;
  if (isDateOnlyValue(value)) return formatDateOnly(value);
  if (isTimestampValue(value)) {
    return String(uiType || '').toLowerCase() === 'datetime' ? formatTimestamp(value) : formatTimestampDate(value);
  }
  return value;
};

/** 'YYYY-MM-DD' for a date input from a stored, display ('MM/DD/YYYY') or timestamp value; '' otherwise. */
export const fieldDateInputValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const s = String(value).trim();
  if (DATE_ONLY.test(s)) return s;
  const us = US_DATE.exec(s);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  if (TIMESTAMP.test(s)) return dayKey(s);
  return '';
};

/** An ExcelJS date cell as 'YYYY-MM-DD', from the parts the sheet shows (its UTC parts). */
export function excelDateText(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** An ExcelJS time cell as 'h:MM:SS AM/PM', from the parts the sheet shows (its UTC parts). */
export function excelTimeText(d) {
  const h = d.getUTCHours();
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  const sec = String(d.getUTCSeconds()).padStart(2, '0');
  return `${h % 12 || 12}:${m}:${sec} ${h >= 12 ? 'PM' : 'AM'}`;
}
